import { useEffect, useState } from 'react';
import { DollarSign, Save, RefreshCw, Loader2 } from 'lucide-react';
import { RFID_SETUP_TYPES, type RfidSetupType } from '@evi/shared';
import {
  fetchRfidPricingSettings,
  updateRfidPricingSettings,
  invalidateRfidPricingCache,
  type RfidPricingSettings,
  type RfidRegistrationPayer,
} from '@/lib/api';
import formStyles from '@/components/ui/Form.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './RfidPricingPage.module.css';

/**
 * RFID Pricing — super_admin-only editor for the one-time RFID tag
 * registration fee matrix (the rfid_pricing settings bucket). This is
 * EVI's own platform revenue, charged platform-only at registration.
 * Modeled on EarningRulesPage: GET the resolved value, edit, PUT back.
 *
 * Amounts only are editable. The payer is structural (driver, operator,
 * or none) and stays fixed. A none-payer channel is free and does not
 * charge regardless of amount, so internal and operator read as free.
 * Reaching this page is gated by the nav (super_admin only) and the server
 * (the PUT is super_admin only); the GET is open, so a non-super_admin who
 * hand-types the URL sees the values but cannot save.
 */

// Human labels per channel. Keys cover every RFID_SETUP_TYPES member.
const CHANNEL_LABELS: Record<RfidSetupType, string> = {
  internal: 'Internal admin',
  tenant_admin: 'Tenant admin',
  building_admin: 'Building admin',
  third_party: 'Third-party admin',
  fleet: 'Fleet',
  operator: 'Operator',
  self_service: 'Driver self-service',
};

interface ChannelRow {
  setupType: RfidSetupType;
  amountDollars: string;          // editable
  payer: RfidRegistrationPayer;   // read-only, structural
  /** Legacy per-tenant switch. No row renders it and the platform no
   *  longer reads it (self-registration is per building since 2026-09-25);
   *  carried only so a save round-trips the stored value. */
  enabled: boolean;
}

function settingsToRows(s: RfidPricingSettings): ChannelRow[] {
  return RFID_SETUP_TYPES.map((t) => {
    const price = s[t];
    return {
      setupType: t,
      amountDollars: price ? (price.amount_cents / 100).toFixed(2) : '0.00',
      payer: price?.payer ?? 'none',
      enabled: price?.enabled ?? false,
    };
  });
}

function rowsToPatch(rows: ChannelRow[]): Partial<RfidPricingSettings> {
  const out: Partial<RfidPricingSettings> = {};
  for (const row of rows) {
    const dollars = Number(row.amountDollars);
    // A non-numeric amount goes through as NaN, which JSON serializes to
    // null, so the server's 400 validation surfaces rather than being
    // silently coerced to 0. The payer is sent unchanged (structural).
    const amount_cents = Number.isFinite(dollars) ? Math.round(dollars * 100) : NaN;
    out[row.setupType] = {
      amount_cents,
      payer: row.payer,
      // Nothing reads it any more; round-tripping it keeps the stored
      // blob unchanged by a price edit.
      enabled: row.enabled,
    };
  }
  return out;
}

export function RfidPricingPage() {
  useRecentRoute('RFID Pricing');
  const [rows, setRows] = useState<ChannelRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    fetchRfidPricingSettings()
      .then((res) => setRows(settingsToRows(res.value)))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const setAmount = (setupType: RfidSetupType, value: string): void => {
    setRows((rs) =>
      rs ? rs.map((r) => (r.setupType === setupType ? { ...r, amountDollars: value } : r)) : rs,
    );
  };

  const handleSave = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!rows) return;
    setSaving(true);
    setError('');
    try {
      const next = await updateRfidPricingSettings(rowsToPatch(rows));
      setRows(settingsToRows(next));
      setSavedAt(Date.now());
    } catch (err) {
      // The api client surfaces the server's VALIDATION_ERROR message on
      // err.message (bad amount, bad payer, unknown channel), so the 400
      // detail reaches the operator.
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleInvalidate = async (): Promise<void> => {
    try {
      await invalidateRfidPricingCache();
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invalidate cache');
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerIcon}><DollarSign size={18} /></div>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>RFID Pricing</h1>
          <p className={styles.subtitle}>
            One-time registration fee per channel. EVI platform revenue, super_admin only.
          </p>
        </div>
      </div>

      {loading && <p className={styles.loadingText}>Loading…</p>}
      {error && <div className={styles.alertErr}>{error}</div>}

      {rows && (
        <form className={styles.card} onSubmit={handleSave}>
          <div className={styles.cardTitleRow}>
            <h2 className={styles.cardTitle}>Registration fees</h2>
            <span className={styles.cacheNote}>Server cache TTL: 60s · changes apply on next registration</span>
          </div>
          <p className={styles.cardLead}>
            The amount charged once when a tag is registered through each channel. The payer is fixed per
            channel. A free channel (payer &ldquo;none&rdquo;) never charges, whatever the amount reads, so
            internal and operator are free. All values are read at runtime, nothing is hardcoded.
          </p>

          {savedAt && (
            <div className={styles.alertOk}>Saved · cache invalidated. New prices take effect on the next registration.</div>
          )}

          <div className={styles.rows}>
            {rows.map((row) => {
              const free = row.payer === 'none';
              const deferred = row.setupType === 'fleet';
              const selfService = row.setupType === 'self_service';
              // F3 (2026-09-25): Site accounts do not issue fobs, so the
              // building_admin and third-party channels are refused at the
              // API. Their prices stay editable only because stored
              // settings and existing cards still carry the setup types.
              const retired = row.setupType === 'building_admin' || row.setupType === 'third_party';
              return (
                <div key={row.setupType} className={styles.row}>
                  <div className={styles.rowInfo}>
                    <span className={styles.channelName}>{CHANNEL_LABELS[row.setupType]}</span>
                    <span className={styles.payerBadge} data-payer={row.payer}>
                      {free ? 'free · no charge' : `${row.payer} pays`}
                    </span>
                    {deferred && (
                      <span className={styles.deferredNote}>not active yet · fleet registration deferred</span>
                    )}
                    {retired && (
                      <span className={styles.deferredNote}>not in use · Site accounts do not issue fobs</span>
                    )}
                    {/* The on/off switch moved to each building (Location
                        settings, F3): this tenant-wide checkbox was one
                        switch for every mirrored building, and the API no
                        longer reads it. The fee here is still what a
                        resident pays. */}
                    {selfService && (
                      <span className={styles.deferredNote}>
                        switched on per building, in each building&apos;s settings
                      </span>
                    )}
                  </div>
                  <div className={styles.amountField}>
                    <span className={styles.dollarSign}>$</span>
                    <input
                      className={styles.amountInput}
                      type="number"
                      step="0.01"
                      min={0}
                      value={row.amountDollars}
                      onChange={(e) => setAmount(row.setupType, e.target.value)}
                      aria-label={`${CHANNEL_LABELS[row.setupType]} registration fee in dollars`}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={handleInvalidate}
              disabled={saving}
              title="Force-clear the server-side cache (no save)"
            >
              <RefreshCw size={13} /> Force refresh cache
            </button>
            <div className={styles.actionsRight}>
              <button type="submit" className={formStyles.btnPrimary} disabled={saving}>
                {saving
                  ? <><Loader2 size={13} /> Saving…</>
                  : <><Save size={13} /> Save changes</>}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
